import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LayoutTemplate } from "lucide-react";
import { errMsg, useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ShopCatalog from "./shop/ShopCatalog";
import ShopCoupons from "./shop/ShopCoupons";
import ShopSettingsForm from "./shop/ShopSettingsForm";
import { Link } from "react-router-dom";

function OnlineCard({ tid, site, page, onChanged, canManage }) {
  const api = useApi();
  const [pages, setPages] = useState([]);
  const [target, setTarget] = useState("");
  useEffect(() => { api.get(`/projects/${site.project.id}`).then(({ data }) => setPages(data.content?.pages || [])).catch(() => setPages([])); }, [api, site.project.id]);
  const toggle = async (enabled) => { try { await api.put(`/t/${tid}/shop/pages`, { site_id: site.id, enabled }); toast.success(enabled ? "Negozio online attivo" : "Negozio online disattivato"); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  const connect = async () => { try { const { data } = await api.post(`/t/${tid}/shop/pages/connect`, { site_id: site.id, page_id: target }); toast.success(`Sezione negozio inserita in «${data.page}»: ricompila il sito per vederla`); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  const bound = page?.bound && pages.find((p) => p.id === page.bound.page_id);
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-4" data-testid="shop-online-card">
      <label className="flex items-center gap-2 text-sm text-slate-800"><Switch checked={!!page?.enabled} onCheckedChange={toggle} disabled={!canManage} data-testid="shop-online-toggle" />Negozio online su questo sito</label>
      {page?.enabled && <>
        <p className="text-xs text-slate-500" data-testid="shop-bound-info">{bound ? `Sezione presente nella pagina «${bound.name}».` : "Nessuna sezione negozio ancora inserita nel sito."}</p>
        {canManage && <div className="flex flex-wrap items-center gap-2">
          <Select value={target} onValueChange={setTarget}><SelectTrigger className="h-8 w-56 text-xs" data-testid="shop-connect-page"><SelectValue placeholder="Scegli la pagina" /></SelectTrigger>
            <SelectContent>{pages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
          <Button size="sm" variant="outline" disabled={!target} onClick={connect} data-testid="shop-connect-btn"><LayoutTemplate size={13} className="mr-1" />Inserisci la sezione</Button>
        </div>}
        <p className="text-[11px] text-slate-500">Il pagamento usa il provider attivo nella scheda «Pagamenti».</p>
      </>}
    </div>
  );
}

export default function SiteShop({ site }) {
  const api = useApi();
  const tid = site.client.id;
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const load = useCallback(async () => {
    try {
      const [t, s, c, p, pg] = await Promise.all([api.get(`/t/${tid}`), api.get(`/t/${tid}/shop/settings`), api.get(`/t/${tid}/shop/categories`), api.get(`/t/${tid}/shop/products`), api.get(`/t/${tid}/shop/pages`)]);
      setData({ role: t.data.role, settings: s.data, categories: c.data, products: p.data.filter((x) => !x.site_ids?.length || x.site_ids.includes(site.id)), page: pg.data.find((x) => x.site_id === site.id) });
    } catch (e) { setData({ error: errMsg(e, "Negozio non caricato") }); }
  }, [api, tid, site.id]);
  useEffect(() => { load(); }, [load, tick]);
  if (!data) return <p className="text-sm text-slate-500">Caricamento…</p>;
  if (data.error) return <p className="text-sm text-red-600" data-testid="site-shop-error">{data.error}</p>;
  const canManage = ["support", "owner", "manager"].includes(data.role);
  const changed = () => setTick((x) => x + 1);
  return (
    <div className="space-y-4" data-testid="site-shop">
      <OnlineCard tid={tid} site={site} page={data.page} onChanged={changed} canManage={canManage} />
      <p className="text-xs text-slate-500" data-testid="shop-crm-note">Ordini e abbonamenti ricevuti si gestiscono nel CRM: <Link className="font-medium text-sky-700 hover:underline" to={`/crm/${tid}?tab=orders`} data-testid="shop-open-orders">Apri Ordini</Link> · <Link className="font-medium text-sky-700 hover:underline" to={`/crm/${tid}?tab=subscriptions`} data-testid="shop-open-subs">Apri Abbonamenti</Link></p>
      <Tabs defaultValue="catalog">
        <div className="max-w-full overflow-x-auto pb-1"><TabsList data-testid="shop-tabs" className="w-max"><TabsTrigger value="catalog" data-testid="shop-tab-catalog">Catalogo</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="shop-tab-coupons">Codici sconto</TabsTrigger>
          <TabsTrigger value="settings" data-testid="shop-tab-settings">Tasse e spedizioni</TabsTrigger></TabsList></div>
        <TabsContent value="catalog" className="pt-3"><ShopCatalog tid={tid} data={data} onChanged={changed} canManage={canManage} /></TabsContent>
        <TabsContent value="coupons" className="pt-3"><ShopCoupons tid={tid} data={data} canManage={canManage} /></TabsContent>
        <TabsContent value="settings" className="pt-3"><ShopSettingsForm key={JSON.stringify(data.settings)} tid={tid} settings={data.settings} onSaved={changed} canManage={canManage} /></TabsContent>
      </Tabs>
    </div>
  );
}
